import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Injector } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { AppInsightsService } from '../services/app-insights-service';
import { v4 as uuidv4 } from 'uuid';

/** Pass untouched request through to the next request handler. */
@Injectable()
export class MonitoringInterceptor implements HttpInterceptor {
  constructor(
    private inj: Injector,
    private appInsightsService: AppInsightsService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const lastTrackName: string = this.appInsightsService.lastTrackName;
    const additionalProperties: any = this.appInsightsService
      .additionalProperties;
    let startTimestamp: number = new Date().getTime();
    const timeoutValue = Number(req.headers.get('timeout')) || 60000;

    return next.handle(req).pipe(
      timeout(timeoutValue),
      tap((event: any) => {
        if (event instanceof HttpResponse && !event.url.endsWith(".svg")) {
          const endTimestamp: number = new Date().getTime();
          if (this.appInsightsService) {
            this.appInsightsService.trackDependencyData(
              this.mapDependecyData(
                req,
                true,
                startTimestamp,
                endTimestamp,
                event as HttpResponse<any>,
                lastTrackName,
                additionalProperties,
                undefined,
                event.status
              )
            );
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        let message: any = {};
        const endTimestamp: number = new Date().getTime();
        const startTimeTemp: number = startTimestamp;        
        
        try {
          if (error.error instanceof Blob) {
            const reader: any = new FileReader();
            reader.onloadend = () => {
              message = JSON.parse(reader.result as string);
              if (this.appInsightsService) {
                this.appInsightsService.trackDependencyData(
                  this.mapDependecyData(
                    req,
                    false,
                    startTimeTemp,
                    endTimestamp,
                    error as HttpErrorResponse,
                    lastTrackName,
                    additionalProperties,
                    message,
                    error.status
                  )
                );
              }
            };
            reader.readAsText(error.error);
          } else {
            message = error.error;

            if (this.appInsightsService) {
              this.appInsightsService.trackDependencyData(
                this.mapDependecyData(
                  req,
                  false,
                  startTimeTemp,
                  endTimestamp,
                  error as HttpErrorResponse,
                  lastTrackName,
                  additionalProperties,
                  message,
                  error.status
                )
              );
            }
          }
        } catch (e) {
          message = {};
        }
        throw error;
      })
    );
  }

  private mapDependecyData(
    request: HttpRequest<any>,
    success: boolean,
    startTimestamp: number,
    endTimestamp: number,
    event: HttpResponse<any> | HttpErrorResponse,
    lastTrackName: string,
    additionalProperties: any,
    responseError?: any,
    status?: number
  ): any {
    const url: string = request.url.replace(`${environment.url.api}`, '');
    const urlWithoutParams: string[] = url.split('?');
    let responseMessage: string = '';

    if (
      responseError &&
      responseError !== {} &&
      responseError.error &&
      responseError.error.message
    ) {
      responseMessage = responseError.error.message;
    }

    return {
      id: uuidv4(),
      name: `${request.method} - ${request.urlWithParams}`,
      duration: endTimestamp - startTimestamp,
      responseCode: status,
      resultCode: status,
      success,
      data: `${request.method} - ${url}`,
      startTime: new Date(startTimestamp),
      properties: {
        ...this.appInsightsService.mapDataToInsights(
          lastTrackName ? lastTrackName : ''
        ),
        HttpMethod: request.method,
        responseHeaders: event.headers,
        requestHeaders: request.headers,
        trackName: lastTrackName ? lastTrackName : '',
        responseText: responseError ? responseError : '',
        responseMessage: responseMessage,
        url,
        urlWithoutParams: urlWithoutParams[0],
        additionalProperties,
      },
      target: request.url,
      type: 'Ajax',
    };
  }
}
